import {
  resolveSpeakingTopicLabel,
  type SpeakingMetricsDoc,
  type TutorAnalyticsSummary,
  type TutorSessionDoc,
} from '../../../tutor/types/tutorAnalytics';
import { resolveSessionDurationSeconds } from './sessionDurationUtils';

/** Normalize raw tutor_type / feature keys to teacher-facing labels. */
export function normalizeTutorTypeKey(raw?: string | null): 'ai_tutor' | 'comprehension' | 'other' {
  const t = String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');
  if (!t) return 'other';
  if (
    t === 'free' ||
    t === 'ai_tutor' ||
    t === 'ai_lesson_tutor' ||
    t === 'live_ai_tutor' ||
    t === 'lesson' ||
    t === 'lesson_tutor'
  ) {
    return 'ai_tutor';
  }
  if (
    t === 'topic' ||
    t === 'communication' ||
    t === 'communication_tutor' ||
    t === 'comprehension' ||
    t === 'reading_comprehension' ||
    t === 'malay_jendela_seni_reading' ||
    t === 'scenario_conversation_tutor' ||
    t === 'scenario_based_convo'
  ) {
    return 'comprehension';
  }
  return 'other';
}

export function tutorTypeLabel(
  raw: string | null | undefined,
  t: (key: string) => string,
): string {
  const key = normalizeTutorTypeKey(raw);
  if (key === 'ai_tutor') return t('dashboard.teacher.studentDrillDown.tutorTypeAiTutor');
  if (key === 'comprehension') return t('dashboard.teacher.studentDrillDown.tutorTypeComprehension');
  if (!String(raw ?? '').trim()) return t('dashboard.teacher.studentDrillDown.tutorTypeUnknown');
  return String(raw);
}

export function tutorTypeBadgeClass(raw?: string | null): string {
  const key = normalizeTutorTypeKey(raw);
  if (key === 'ai_tutor') {
    return 'bg-purple-100 text-app-primary dark:bg-purple-900 dark:text-purple-200';
  }
  if (key === 'comprehension') {
    return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
  }
  return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200';
}

/** Teacher-facing feedback language preference for speaking evaluations. */
export type TeacherFeedbackLanguage = 'auto' | 'en' | 'ms';

const TAMIL_SCRIPT_RE = /[\u0B80-\u0BFF]/;
const MALAY_FEEDBACK_FALLBACK =
  'Usaha yang baik! Teruskan berlatih bercakap dalam Bahasa Melayu.';
const MALAY_RECOMMENDATION_FALLBACKS = [
  'Berlatih bercakap dalam Bahasa Melayu setiap hari',
  'Gunakan perkataan baharu dalam ayat pendek',
];

export function containsTamilScript(text: string | null | undefined): boolean {
  return TAMIL_SCRIPT_RE.test(String(text || ''));
}

function englishScoreSummary(
  session: Pick<SpeakingMetricsDoc, 'scores'>,
): string {
  const s = session.scores;
  if (!s) return '';
  return `Overall ${s.overall}%. Pronunciation ${s.pronunciation}%, Fluency ${s.fluency}%, Grammar ${s.grammar}%, Vocabulary ${s.vocabulary}%.`;
}

function englishFeedbackFallback(
  session: Pick<SpeakingMetricsDoc, 'recommendations' | 'scores'>,
  allowRecommendationFallback = true,
): string {
  if (allowRecommendationFallback) {
    const recs = (session.recommendations ?? [])
      .map((r) => String(r).trim())
      .filter((r) => r && !containsTamilScript(r));
    if (recs.length) return recs.join(' ');
  }
  return englishScoreSummary(session);
}

export function findRelatedSessionHistory(
  speaking: Pick<SpeakingMetricsDoc, 'session_id'> & { created_at?: string | null },
  sessionHistory: TutorSessionDoc[] | undefined,
): TutorSessionDoc | undefined {
  const rows = sessionHistory ?? [];
  const sid = String(speaking.session_id || '').trim();
  if (sid) {
    const exact = rows.find((s) => String(s.session_id || '').trim() === sid);
    if (exact) return exact;
  }
  const speakingMs = speaking.created_at ? new Date(speaking.created_at).getTime() : NaN;
  if (!Number.isFinite(speakingMs)) return undefined;
  let best: TutorSessionDoc | undefined;
  let bestDelta = Infinity;
  for (const row of rows) {
    const rowMs = new Date(row.started_at || row.ended_at || '').getTime();
    if (!Number.isFinite(rowMs)) continue;
    const delta = Math.abs(rowMs - speakingMs);
    if (delta <= 3 * 60 * 1000 && delta < bestDelta) {
      bestDelta = delta;
      best = row;
    }
  }
  return best;
}

export function resolveSpeakingSessionDurationSeconds(
  speaking: {
    duration_seconds?: number | null;
    started_at?: string | null;
    ended_at?: string | null;
    created_at?: string | null;
    session_id?: string;
  },
  sessionHistory?: TutorSessionDoc[],
): number | null {
  const direct = resolveSessionDurationSeconds(speaking);
  if (direct) return direct;
  const related = findRelatedSessionHistory(
    { session_id: speaking.session_id || '', created_at: speaking.created_at },
    sessionHistory,
  );
  return related ? resolveSessionDurationSeconds(related) : null;
}

export function resolveTeacherSpeakingTopicLabel(
  speaking: Pick<SpeakingMetricsDoc, 'session_id' | 'topic_label'> & {
    tutor_type?: string | null;
    created_at?: string | null;
  },
  sessionHistory: TutorSessionDoc[] | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const direct = resolveSpeakingTopicLabel(speaking, sessionHistory ?? []);
  if (direct) return direct;
  const related = findRelatedSessionHistory(speaking, sessionHistory);
  const fromRelated = related?.topic_label?.trim();
  if (fromRelated) return fromRelated;
  if (related?.lesson_no != null && Number.isFinite(Number(related.lesson_no))) {
    return t('dashboard.teacher.studentDrillDown.lessonLabel', { n: related.lesson_no });
  }
  const typeRaw = speaking.tutor_type || related?.tutor_type || related?.mode;
  if (typeRaw && String(typeRaw).trim() && String(typeRaw).toLowerCase() !== 'unknown') {
    return tutorTypeLabel(typeRaw, t);
  }
  return '';
}

/** Resolve learner feedback for teachers (Malay/Tamil native text + optional English). */
export function resolveTeacherSpeakingFeedback(
  session: Pick<SpeakingMetricsDoc, 'feedback_ta' | 'recommendations' | 'scores'> & {
    feedback_en?: string | null;
  },
  uiLanguage: string,
  feedbackLanguage: TeacherFeedbackLanguage = 'auto',
  isMalayTenant = false,
): string {
  const en = String(session.feedback_en ?? '').trim();
  const native = String(session.feedback_ta ?? '').trim();
  const hideTamil = isMalayTenant || feedbackLanguage === 'ms';
  const nativeUsable = Boolean(native) && !(hideTamil && containsTamilScript(native));
  const prefer =
    feedbackLanguage === 'auto'
      ? isMalayTenant
        ? 'ms'
        : uiLanguage.toLowerCase().startsWith('en')
          ? 'en'
          : 'native'
      : feedbackLanguage;

  if (prefer === 'en') {
    if (en) return en;
    const fallback = englishFeedbackFallback(session, !isMalayTenant);
    if (fallback) return fallback;
    return isMalayTenant ? englishScoreSummary(session) : nativeUsable ? native : '';
  }

  if (prefer === 'ms' || isMalayTenant) {
    if (nativeUsable) return native;
    if (en) return en;
    return MALAY_FEEDBACK_FALLBACK;
  }

  if (nativeUsable) return native;
  if (en) return en;
  return englishFeedbackFallback(session);
}

export function resolveTeacherSpeakingRecommendations(
  session: Pick<SpeakingMetricsDoc, 'recommendations'>,
  isMalayTenant = false,
): string[] {
  const recs = (session.recommendations ?? []).map((r) => String(r).trim()).filter(Boolean);
  if (!isMalayTenant) return recs;
  const malayRecs = recs.filter((r) => !containsTamilScript(r));
  if (malayRecs.length) return malayRecs;
  return recs.length ? [...MALAY_RECOMMENDATION_FALLBACKS] : [];
}

export function resolveLatestOverallScore(data: TutorAnalyticsSummary | null | undefined): number | null {
  if (!data) return null;
  const latest = data.latest_overall_score;
  if (latest !== null && latest !== undefined && Number.isFinite(Number(latest))) {
    return Number(latest);
  }
  const first = data.speaking_sessions?.[0]?.scores?.overall;
  if (first !== null && first !== undefined && Number.isFinite(Number(first))) {
    return Number(first);
  }
  return null;
}

type LastLessonSummary = {
  last_lesson_no?: number | null;
  last_topic_label?: string | null;
};

/** Teacher table cell for last lesson / last activity. */
export function formatLastLessonSummary(
  summary: LastLessonSummary | null | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (!summary) return '—';
  if (summary.last_lesson_no != null && Number.isFinite(Number(summary.last_lesson_no))) {
    return t('tutor.analytics.lessonN', { n: summary.last_lesson_no });
  }
  const topic = String(summary.last_topic_label ?? '').trim();
  return topic || '—';
}

export function parseIsoDay(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d : null;
}

export function filterAnalyticsByDateRange(
  data: TutorAnalyticsSummary,
  start: Date | null,
  end: Date | null,
): TutorAnalyticsSummary {
  if (!start && !end) return data;
  const startMs = start ? new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime() : null;
  const endMs = end
    ? new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999).getTime()
    : null;
  const inRange = (iso: string | null | undefined) => {
    const d = parseIsoDay(iso);
    if (!d) return !startMs && !endMs;
    const ms = d.getTime();
    if (startMs != null && ms < startMs) return false;
    if (endMs != null && ms > endMs) return false;
    return true;
  };
  return {
    ...data,
    speaking_sessions: (data.speaking_sessions ?? []).filter((s) => inRange(s.created_at)),
    daily_engagement: (data.daily_engagement ?? []).filter((d) => inRange(`${d.date}T12:00:00`)),
    session_history: (data.session_history ?? []).filter((s) => inRange(s.started_at)),
    trend: (data.trend ?? []).filter((p) => inRange(`${p.date}T12:00:00`)),
  };
}

export function aggregateTutorTypeCounts(
  sessions: TutorSessionDoc[],
  t: (key: string) => string,
): Array<{ label: string; value: number }> {
  const map = new Map<'ai_tutor' | 'comprehension' | 'other', number>();
  for (const s of sessions) {
    const key = normalizeTutorTypeKey(s.tutor_type);
    map.set(key, (map.get(key) ?? 0) + 1);
  }
  return Array.from(map.entries()).map(([key, value]) => ({
    label:
      key === 'ai_tutor'
        ? t('dashboard.teacher.studentDrillDown.tutorTypeAiTutor')
        : key === 'comprehension'
          ? t('dashboard.teacher.studentDrillDown.tutorTypeComprehension')
          : t('dashboard.teacher.studentDrillDown.tutorTypeUnknown'),
    value,
  }));
}

export function humanizeSkillKey(skillKey: string): string {
  const tail = skillKey.includes('.') ? skillKey.split('.').slice(1).join(' ') : skillKey;
  return tail.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
